// Reusable in-place paywall popup for prelanders — mirrors the platform
// NewPaywallPopup in plain React/CSS. Lander-specific values come in via props.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSubscriptionPlans } from './useSubscriptionPlans';
import { useUpgateCheckout } from './useUpgateCheckout';
import { useTokenBonus } from './useTokenBonus';
import { useGrowthBookValues } from '../../contexts/GrowthBookContext';
import { useCurrentCurrency } from './useCurrentCurrency';
import { trackPopupOrderCreated } from './orderTracking';

// Trust/benefit assets copied 1:1 from the platform
// (frontend/public/v3/new-paywall-popup → lander public/v3/new-paywall-popup).
// CRA serves public/ at the site root, so the same absolute paths resolve.
const ASSET_BASE = '/v3/new-paywall-popup';

// Benefit rows: left glyph (exact platform px sizes) + label with bold emphasis.
const BENEFITS = [
  {
    key: 'cumslut',
    icon: `${ASSET_BASE}/benefit-cumslut.svg`,
    iconW: 12.8,
    iconH: 14.4,
    // create-friend-white is the SFW lander variant → SFW copy.
    label: <>Create your ideal companion</>,
  },
  {
    key: 'controllable',
    icon: `${ASSET_BASE}/benefit-controllable.svg`,
    iconW: 13.47,
    iconH: 16,
    label: (
      <>
        100% <strong>controlable</strong> and <strong>anonymous</strong>
      </>
    ),
  },
  {
    key: 'chat',
    icon: `${ASSET_BASE}/benefit-chat.svg`,
    iconW: 13.33,
    iconH: 13.33,
    label: (
      <>
        Unlock <strong>Unlimited</strong> Chat Messages
      </>
    ),
  },
  {
    // label rendered dynamically from monthlyTokensBonus (settings/GB) — the
    // monthly bonus is global, not per-plan, so it doesn't react to selection.
    key: 'tokens',
    icon: `${ASSET_BASE}/benefit-tokens.svg`,
    iconW: 11.74,
    iconH: 11.77,
    label: null,
  },
  {
    key: 'girlfriends',
    icon: `${ASSET_BASE}/benefit-girlfriends.svg`,
    iconW: 16,
    iconH: 11.93,
    label: (
      <>
        Create <strong>Unlimited</strong> AI Girlfriends
      </>
    ),
  },
];

// Payment-trust logos, exact platform display sizes.
const PAYMENT_LOGOS = [
  { src: `${ASSET_BASE}/visa.png`, alt: 'Visa', w: 28.32, h: 8.65 },
  {
    src: `${ASSET_BASE}/mastercard.png`,
    alt: 'Mastercard',
    w: 27.06,
    h: 16.86,
  },
  { src: `${ASSET_BASE}/apple-pay.png`, alt: 'Apple Pay', w: 38.66, h: 14.83 },
  {
    src: `${ASSET_BASE}/google-pay.png`,
    alt: 'Google Pay',
    w: 40.12,
    h: 18.09,
  },
];

// Gradient-filled checkmark (reuses the platform check.svg path, 16x12 in 20x20).
function GradientCheck() {
  return (
    <svg
      className="paywall-popup__check"
      width={20}
      height={20}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id="paywallPopupCheckGradient"
          x1="3.34"
          y1="5.18"
          x2="16.66"
          y2="14.51"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.0657" stopColor="#B14CEE" />
          <stop offset="1" stopColor="#8049F3" />
        </linearGradient>
      </defs>
      <path
        d="M15.4848 5.18408C15.233 5.18408 14.9813 5.27945 14.7906 5.47008L8.17175 12.089L5.20928 9.12651C4.828 8.74524 4.20211 8.74524 3.82084 9.12651L3.62775 9.3196C3.24648 9.70088 3.24648 10.3268 3.62775 10.708L7.42943 14.5097C7.83726 14.9176 8.5062 14.9176 8.91405 14.5097L16.3722 7.05161C16.7534 6.67035 16.7534 6.04444 16.3722 5.66317L16.1791 5.47008C15.9884 5.27945 15.7367 5.18408 15.4848 5.18408Z"
        fill="url(#paywallPopupCheckGradient)"
      />
    </svg>
  );
}

function HeartIcon() {
  return (
    <img
      className="paywall-popup__heart"
      src={`${ASSET_BASE}/heart-small.svg`}
      alt=""
      width={12}
      height={12}
      aria-hidden="true"
    />
  );
}

// Per-day price split, matching the platform getPriceCalculations:
//   pricePerDay = paymentAmount / (duration_months * 30); toFixed(2).split('.')
// bonusTokens is computed by useTokenBonus (settings + instant + plan extra).
function getPlanView(plan, currencySymbol, bonusTokens) {
  const totalPrice = plan.paymentAmount;
  const pricePerDay = totalPrice / (plan.duration_months * 30);
  const [whole, cents] = pricePerDay.toFixed(2).split('.');
  return {
    id: plan._id,
    // Capitalized "Month(s)" to match the platform's i18n labels (3 Months / 1 Month).
    durationLabel: `${plan.duration_months} ${
      plan.duration_months > 1 ? 'Months' : 'Month'
    }`,
    priceWhole: `${currencySymbol}${whole}`,
    priceCents: `.${cents}`,
    discount: plan.discount_percent ? `-${plan.discount_percent}%` : null,
    bonusTokens,
  };
}

function PlanCard({ view, isSelected, onSelect }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`paywall-popup__card${
        isSelected ? ' paywall-popup__card--selected' : ''
      }`}
    >
      {view.discount && (
        <span className="paywall-popup__sale">
          <span className="paywall-popup__sale-text">{view.discount}</span>
        </span>
      )}
      <span className="paywall-popup__card-inner">
        <span className="paywall-popup__duration">{view.durationLabel}</span>
        <span className="paywall-popup__price-block">
          <span className="paywall-popup__price">
            <span className="paywall-popup__price-whole">
              {view.priceWhole}
            </span>
            <span className="paywall-popup__price-cents">
              {view.priceCents}
            </span>
          </span>
          <span className="paywall-popup__per-day">per day</span>
        </span>
        <span className="paywall-popup__heart-pill">
          <HeartIcon />
          <span className="paywall-popup__heart-amount">
            +{view.bonusTokens}
          </span>
        </span>
      </span>
    </button>
  );
}

// Payment-trust row using the real platform PNGs. PCI + SSL are their own
// group with a tighter gap, matching the platform layout.
function PaymentIcons() {
  return (
    <div className="paywall-popup__payments">
      {PAYMENT_LOGOS.map((logo) => (
        <img
          key={logo.alt}
          className="paywall-popup__payment-logo"
          src={logo.src}
          alt={logo.alt}
          style={{ width: logo.w, height: logo.h }}
        />
      ))}
      <span className="paywall-popup__payment-group">
        <img
          className="paywall-popup__payment-logo"
          src={`${ASSET_BASE}/pci.png`}
          alt="PCI"
          style={{ width: 27.53, height: 17.92 }}
        />
        <img
          className="paywall-popup__payment-logo"
          src={`${ASSET_BASE}/ssl.png`}
          alt="SSL Encripted"
          style={{ width: 82.88, height: 14.16 }}
        />
      </span>
    </div>
  );
}

function VideoHeader({ src }) {
  const [isLoaded, setIsLoaded] = useState(false);
  return (
    <div className="paywall-popup__video-wrap">
      {src && (
        <video
          key={src}
          src={src}
          onCanPlay={() => setIsLoaded(true)}
          className="paywall-popup__video"
          autoPlay
          controls={false}
          loop
          playsInline
          muted
          preload="auto"
        />
      )}
      <div className="paywall-popup__video-blend paywall-popup__video-blend--top" />
      <div className="paywall-popup__video-blend paywall-popup__video-blend--bottom" />
      {src && !isLoaded && <div className="paywall-popup__video-loader" />}
    </div>
  );
}

// authToken authorizes checkout; videoSrc/instantTokens/currencySymbol theme it;
// instantTokens overrides the GB per-duration bonus; onCheckout overrides the call.
// currencySymbol overrides the geo-detected symbol (GET /payments/currency).
//
// paywallOrder ('after_signup' | 'popup_first') rides on the popup's analytics so
// every event is attributable to the arm the user saw. In the 'popup_first' arm the
// popup renders for an UNAUTHENTICATED visitor (no authToken yet) purely to collect
// the plan choice, so the caller passes `onCheckout` (capture instead of charge) and
// `trackOrder={false}` — order_created is fired later, once registration produced a
// userId, so the funnel keeps registration_started → order_created ordering. None of
// the popup's own data (plans / system-settings / geo currency / GB token bonuses)
// needs a user, so an anonymous render shows the same prices and instant-token
// amounts an authenticated one would.
function PaywallPopup({
  authToken,
  userId,
  videoSrc,
  instantTokens,
  currencySymbol,
  onCheckout,
  // Checkout (Continue) failed. The popup keeps its own recovery — the button
  // re-enables so the user can retry — this only lets the parent react to an
  // unrecoverable one, i.e. a dead authToken on a resumed session.
  onCheckoutError,
  paywallType,
  paywallOrder,
  trackOrder = true,
  email,
  clickIds,
}) {
  const { plans, isLoading } = useSubscriptionPlans();
  const { i18n } = useTranslation();
  const checkoutHook = useUpgateCheckout({ authToken });
  const { getBonusForPlan, monthlyTokensBonus } = useTokenBonus({
    instantTokensOverride: instantTokens,
  });
  // The plans endpoint is unauthenticated here, so it can't tell us the visitor's
  // rebill variant — nor apply their geo price multiplier to the prices it returns.
  // Both come from the lander's own GrowthBook instead.
  const { isFlatRebill, priceMultiplier } = useGrowthBookValues();
  const geoCurrency = useCurrentCurrency();
  const symbol = currencySymbol ?? geoCurrency.symbol;
  // ISO code (USD/EUR/…) for analytics `currency`; `symbol` stays display-only.
  const currencyCode = geoCurrency.code;

  const sortedPlans = useMemo(
    () => [...plans].sort((a, b) => a.order - b.order),
    [plans],
  );

  const [selectedId, setSelectedId] = useState(null);
  // One-shot guard: order_created must fire at most once per popup, even if the
  // user re-taps Continue after a failed checkout (mirrors the platform, which
  // guards trackGTMOrderCreated behind an orderCreatedTracked flag).
  const orderTrackedRef = useRef(false);

  // Default selection: 12-month if present, else first by order.
  useEffect(() => {
    if (!sortedPlans.length || selectedId) return;
    const yearly = sortedPlans.find((p) => p.duration_months === 12);
    setSelectedId((yearly ?? sortedPlans[0])._id);
  }, [sortedPlans, selectedId]);

  const handleContinue = async () => {
    if (checkoutHook.isLoading) return;
    const plan = sortedPlans.find((p) => p._id === selectedId);
    if (!plan) return;
    // order_created (all three sinks) — see ./orderTracking.js. One-shot so re-taps
    // after a failed checkout don't re-emit and inflate the funnel. Skipped when the
    // caller defers it (trackOrder={false}, the popup_first plan-capture render).
    if (trackOrder && !orderTrackedRef.current) {
      orderTrackedRef.current = true;
      trackPopupOrderCreated({
        plan,
        currencyCode,
        paywallType,
        paywallOrder,
        userId,
        email,
        clickIds,
      });
    }
    const checkoutOpts = {
      bonusTokens: getBonusForPlan(plan),
      currencySymbol: symbol,
      currencyCode,
      locale: i18n.language,
      // Forwarded onto the /lobby return URL so the platform tags
      // subscription_purchase with the experiment variant (all popup methods).
      paywallType,
      isFlatRebill,
      priceMultiplier,
    };
    try {
      if (onCheckout) {
        // The override receives everything it needs to run (or defer) the charge
        // itself — popup_first persists these and charges after registration.
        await onCheckout(plan, { ...checkoutOpts, checkoutHook });
      } else {
        await checkoutHook.checkout(plan, checkoutOpts);
      }
    } catch (err) {
      console.error('[PaywallPopup] checkout failed', err);
      if (onCheckoutError) onCheckoutError(err);
    }
  };

  // Portal to <body> so the fixed-position overlay escapes the host (the auth
  // modal's subtree has backdrop-filter/transform ancestors that would otherwise
  // become the containing block, trapping the popup inside a tiny box).
  return createPortal(
    <div className="paywall-popup-overlay">
      <div className="paywall-popup" data-content="paywallPopup">
        <VideoHeader src={videoSrc} />

        <div className="paywall-popup__body">
          <ul className="paywall-popup__benefits">
            {BENEFITS.map((benefit) => (
              <li key={benefit.key} className="paywall-popup__benefit">
                <span className="paywall-popup__benefit-main">
                  <span className="paywall-popup__benefit-icon">
                    <img
                      src={benefit.icon}
                      alt=""
                      aria-hidden="true"
                      style={{ width: benefit.iconW, height: benefit.iconH }}
                    />
                  </span>
                  <span className="paywall-popup__benefit-label">
                    {benefit.key === 'tokens' ? (
                      <>
                        <strong>{monthlyTokensBonus || 200} Free</strong> tokens
                        monthly
                      </>
                    ) : (
                      benefit.label
                    )}
                  </span>
                </span>
                <GradientCheck />
              </li>
            ))}
          </ul>

          {sortedPlans.length > 0 ? (
            <div className="paywall-popup__cards">
              {sortedPlans.map((plan) => (
                <PlanCard
                  key={plan._id}
                  view={getPlanView(plan, symbol, getBonusForPlan(plan))}
                  isSelected={selectedId === plan._id}
                  onSelect={() => setSelectedId(plan._id)}
                />
              ))}
            </div>
          ) : (
            <div
              className="paywall-popup__cards-placeholder"
              aria-busy={isLoading}
            />
          )}

          <button
            type="button"
            className="paywall-popup__continue"
            onClick={handleContinue}
            disabled={checkoutHook.isLoading || !selectedId}
          >
            {checkoutHook.isLoading ? 'Loading…' : 'Continue'}
          </button>

          <PaymentIcons />
        </div>
      </div>
    </div>,
    document.body,
  );
}

PaywallPopup.propTypes = {
  authToken: PropTypes.string,
  userId: PropTypes.string,
  videoSrc: PropTypes.string,
  instantTokens: PropTypes.string,
  currencySymbol: PropTypes.string,
  onCheckout: PropTypes.func,
  onCheckoutError: PropTypes.func,
  paywallType: PropTypes.string,
  paywallOrder: PropTypes.string,
  trackOrder: PropTypes.bool,
  email: PropTypes.string,
  clickIds: PropTypes.object,
};

export default PaywallPopup;
